import { useState, useEffect, useRef, useCallback } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useSelector, useDispatch } from "react-redux";
import {
  setClientSecret,
  setOrderId,
  setOrderNumber,
  setCheckoutSession,
  setActiveStep,
  setPaymentCompleted,
  setLoading,
  setError,
  updateCheckoutField as updateCheckoutFieldAction,
  setSameAsShipping as setSameAsShippingAction,
  resetCheckout as resetCheckoutAction,
  setPrefillCompleted as setPrefillCompletedAction,
} from "../store/slices/checkoutSlice";
import checkoutService from "../services/checkoutService";
import { useCart } from "../contexts/CartContext";
import { useAuth } from "../contexts/AuthContext";
import { loadStripe } from "@stripe/stripe-js";
import axiosInstance from "../services/axiosConfig";
import { useTranslation } from "react-i18next";

// Single stripe promise for the whole application
let stripePromiseSingleton = null;

/**
 * Custom hook for managing checkout process using Redux store
 * @returns {Object} Checkout state and handlers
 */
export const useCheckout = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation(); // Get current location to detect navigation
  const { cart, clearCart, fetchCart } = useCart();
  const { user } = useAuth();
  const { t } = useTranslation("hooks");

  // Get checkout state from Redux
  const {
    clientSecret,
    orderId,
    orderNumber,
    checkoutSession,
    activeStep,
    paymentCompleted,
    loading,
    error,
    checkoutData,
    prefillCompleted,
  } = useSelector((state) => state.checkout);

  // Local state for Stripe initialization
  const [stripeInitialized, setStripeInitialized] = useState(
    !!stripePromiseSingleton
  );
  const [stripeError, setStripeError] = useState(null);

  /**
   * Initialize Stripe with the public key
   * Ensures Stripe is loaded only once.
   */
  const initStripe = useCallback(async () => {
    // If already initialized (globally or in this hook's state), return the promise
    if (stripePromiseSingleton) {
      if (!stripeInitialized) setStripeInitialized(true);
      console.log("Stripe already initialized, returning singleton promise.");
      return stripePromiseSingleton;
    }

    // Prevent multiple simultaneous initializations
    if (initStripe.initializing) {
      console.log("Stripe initialization already in progress, waiting...");
      // Wait for the existing initialization to complete
      return await new Promise((resolve) => {
        const interval = setInterval(() => {
          if (stripePromiseSingleton) {
            clearInterval(interval);
            resolve(stripePromiseSingleton);
          }
        }, 100);
      });
    }

    try {
      initStripe.initializing = true;
      console.log("Attempting to initialize Stripe...");
      // Get the Stripe public key from environment variables
      const stripePublicKey = import.meta.env.VITE_STRIPE_PUBLIC_KEY;

      // In development, provide a fallback test key if not found
      const isDevelopment = import.meta.env.MODE === "development";
      const finalStripeKey =
        stripePublicKey ||
        (isDevelopment
          ? "pk_test_51NXwUXQ2nZA5QXYMwbAQLhUkSdpJ3xnd8FB4P8swrQnBVxkWLJYnwSJ7K1KeROqCOcZUz5tCUcS1xeGdTxTDf0Cj00JYgRlkNl" // Replace with your actual test key if needed
          : null);

      if (!finalStripeKey) {
        throw new Error("Stripe public key not found in environment variables");
      }

      console.log(
        `Initializing Stripe with ${
          isDevelopment && !stripePublicKey
            ? "fallback test key"
            : "public key from env"
        }...`
      );

      // Load the Stripe.js library dynamically
      const stripeInstance = await loadStripe(finalStripeKey);

      if (!stripeInstance) {
        throw new Error("Failed to load Stripe.js");
      }

      // Store in singleton for reuse across hook instances
      stripePromiseSingleton = stripeInstance;

      // Update local state
      setStripeInitialized(true);
      setStripeError(null);
      console.log("Stripe initialized successfully.");
      return stripeInstance;
    } catch (error) {
      console.error("Error initializing Stripe:", error);
      setStripeError(error.message);
      setStripeInitialized(false);
      return null;
    } finally {
      initStripe.initializing = false;
    }
  }, [stripeInitialized]);

  // Flag to track if initialization has been attempted
  const initAttempted = useRef(false);

  // Initialize Stripe once when the hook is first mounted
  useEffect(() => {
    if (!initAttempted.current && !stripePromiseSingleton) {
      initAttempted.current = true;
      console.log("useCheckout: Initializing Stripe on mount...");
      initStripe().catch((err) => {
        // Error is already logged and state updated in initStripe
        dispatch(setError(t("checkout.stripeInitError")));
      });
    } else if (stripePromiseSingleton && !stripeInitialized) {
      // Sync local state if singleton exists but local state is false
      setStripeInitialized(true);
    }
  }, [initStripe, dispatch, stripeInitialized, t]);

  // Pre-fill form data from user profile
  useEffect(() => {
    // Only run prefill if:
    // 1. We have a user profile
    // 2. We are on the Address step (activeStep === 0)
    // 3. The prefill hasn't been completed yet for this checkout instance
    if (user && activeStep === 0 && !prefillCompleted) {
      console.log(
        "Prefill effect triggered (user && activeStep 0 && !prefillCompleted): filling address data"
      );

      const hasShippingAddress =
        user.shippingAddress && user.shippingCity && user.shippingPostalCode;

      const hasBillingAddress =
        user.billingAddress && user.billingCity && user.billingPostalCode;

      // Use a temporary object to collect updates
      const updates = [];

      // Fill shipping address from user profile if available
      if (hasShippingAddress) {
        console.log("Prefilling shipping address from profile");
        updates.push({
          section: "shippingAddress",
          field: "address",
          value: user.shippingAddress || "",
        });
        updates.push({
          section: "shippingAddress",
          field: "city",
          value: user.shippingCity || "",
        });
        updates.push({
          section: "shippingAddress",
          field: "postalCode",
          value: user.shippingPostalCode || "",
        });
        updates.push({
          section: "shippingAddress",
          field: "country",
          value: user.shippingCountry || "CH",
        });
      }

      // Handle billing address based on user profile
      if (hasBillingAddress) {
        console.log(
          "User has billing address in profile, setting sameAsShipping to false"
        );
        // Set sameAsShipping to false first
        dispatch(setSameAsShippingAction(false));

        // Fill billing address fields
        updates.push({
          section: "billingAddress",
          field: "address",
          value: user.billingAddress || "",
        });
        updates.push({
          section: "billingAddress",
          field: "city",
          value: user.billingCity || "",
        });
        updates.push({
          section: "billingAddress",
          field: "postalCode",
          value: user.billingPostalCode || "",
        });
        updates.push({
          section: "billingAddress",
          field: "country",
          value: user.billingCountry || "CH",
        });
      } else {
        // If user doesn't have a billing address, ensure sameAsShipping is true
        console.log(
          "No billing address in profile, setting sameAsShipping to true"
        );
        dispatch(setSameAsShippingAction(true));
      }

      // Dispatch all updates collected
      updates.forEach((update) => dispatch(updateCheckoutFieldAction(update)));

      // Mark prefill as completed for this session
      console.log("Marking prefill as completed.");
      dispatch(setPrefillCompletedAction(true));
    }
    // Dependencies: user, prefillCompleted, dispatch, activeStep.
  }, [user, prefillCompleted, dispatch, activeStep]);

  /**
   * Update checkout form fields in Redux state
   */
  const updateCheckoutField = useCallback(
    (section, field, value) => {
      dispatch(
        updateCheckoutFieldAction({
          section,
          field,
          value,
        })
      );
    },
    [dispatch]
  );

  /**
   * Handle 'Same as Shipping' checkbox change for billing address
   */
  const handleSameAsShipping = useCallback(
    (checked) => {
      dispatch(setSameAsShippingAction(checked));
    },
    [dispatch]
  );

  /**
   * Prepare final checkout data before submitting to the backend
   * @returns {Object} Prepared checkout data for the API
   */
  const prepareCheckoutDataForApi = useCallback(() => {
    const { billingAddress, ...restData } = checkoutData;

    // ✅ FIX: More robust billing address preparation
    let finalBillingAddress;

    if (billingAddress.sameAsShipping) {
      // Always use shipping address when sameAsShipping is true
      finalBillingAddress = {
        address: checkoutData.shippingAddress.address || "",
        city: checkoutData.shippingAddress.city || "",
        postalCode: checkoutData.shippingAddress.postalCode || "",
        country: checkoutData.shippingAddress.country || "CH",
      };

      console.log(
        "prepareCheckoutDataForApi: Using shipping address for billing (sameAsShipping=true):",
        finalBillingAddress
      );
    } else {
      // Use the entered billing address fields
      finalBillingAddress = {
        address: billingAddress.address || "",
        city: billingAddress.city || "",
        postalCode: billingAddress.postalCode || "",
        country: billingAddress.country || "CH",
      };

      console.log(
        "prepareCheckoutDataForApi: Using separate billing address (sameAsShipping=false):",
        finalBillingAddress
      );
    }

    const preparedData = {
      ...restData,
      billingAddress: finalBillingAddress,
      // Add explicit flag for backend to know when billing should be same as shipping
      billingIsSameAsShipping: billingAddress.sameAsShipping,
    };

    console.log("prepareCheckoutDataForApi: Final prepared data:", {
      shippingAddress: preparedData.shippingAddress,
      billingAddress: preparedData.billingAddress,
      billingIsSameAsShipping: preparedData.billingIsSameAsShipping,
      paymentMethod: preparedData.paymentMethod,
    });

    return preparedData;
  }, [checkoutData]);

  /**
   * Function to navigate to the confirmation step
   * This is separated to be called explicitly after instructions are shown for bank transfer.
   */
  const goToConfirmation = useCallback(() => {
    dispatch(setLoading(false));
    dispatch(setActiveStep(2)); // Move to step 2 (Order Complete/Review)
    // Payment is not truly completed for bank transfers at this stage.
    // The paymentCompleted flag is only set on successful online payment confirmation.
    // Do NOT clear cart here for bank transfer, only on actual payment success (webhook/manual)
  }, [dispatch]);

  /**
   * Create a checkout session with the backend API
   * This is the first step in the checkout process, where we send address and basic info
   * @returns {Promise<void>}
   */
  const createCheckoutSession = async () => {
    try {
      // ✅ FIX: Safety check - ensure billing address is synced if sameAsShipping is true
      if (checkoutData.billingAddress.sameAsShipping) {
        console.log(
          "createCheckoutSession: Safety check - ensuring billing address is synced"
        );
        dispatch(setSameAsShippingAction(true)); // This will trigger the sync

        // Small delay to ensure Redux state is updated
        await new Promise((resolve) => setTimeout(resolve, 10));
      }

      const preparedData = prepareCheckoutDataForApi();

      console.log(
        "useCheckout: Creating checkout session with data:",
        JSON.stringify(preparedData, null, 2)
      );
      dispatch(setLoading(true));
      dispatch(setError(null));

      const response = await checkoutService.createCheckoutSession(
        preparedData
      );
      console.log("useCheckout: Checkout session created:", response);

      // Store response data in Redux
      dispatch(setOrderId(response.orderId));
      dispatch(setOrderNumber(response.orderNumber));
      dispatch(setClientSecret(response.clientSecret));
      dispatch(setCheckoutSession(response));

      // If the checkout indicates it's fully paid by wallet
      if (response.paidByWalletOnly) {
        console.log(
          "useCheckout: Order fully paid by wallet, moving to confirmation"
        );
        dispatch(setPaymentCompleted(true));
        dispatch(setActiveStep(2));
      } else {
        // Move to payment step otherwise
        console.log("useCheckout: Moving to payment step");
        dispatch(setActiveStep(1));
      }

      dispatch(setLoading(false));
    } catch (error) {
      console.error("useCheckout: Error creating checkout session:", error);
      dispatch(setError(error.message || t("checkout.sessionCreateError")));
      dispatch(setLoading(false));
    }
  };

  /**
   * Process payment and confirm with the backend
   * @param {Object} options - Options for payment process
   * @param {string} [options.paymentMethod] - Override payment method from checkoutData
   * @param {string} [options.paymentIntentId] - Payment intent ID from Stripe
   * @param {string} [options.orderId] - Explicitly pass the Order ID (especially after redirect)
   * @returns {Promise<boolean>} Success status
   */
  const processPayment = async (options = {}) => {
    // Get the orderId either from options or from the Redux state
    const currentOrderId = options.orderId || orderId; // Use passed orderId first

    try {
      console.log(
        `useCheckout: Processing payment confirmation for method: ${
          checkoutData.paymentMethod
        }, paymentIntentId: ${options.paymentIntentId || "none"}, orderId: ${
          currentOrderId || "missing"
        }`
      );
      dispatch(setLoading(true));
      dispatch(setError(null));

      if (!currentOrderId) {
        // Check the derived currentOrderId
        throw new Error("Order ID is missing, cannot process payment.");
      }

      let confirmationData = { orderId: currentOrderId }; // Use currentOrderId
      let paymentConfirmed = false;

      // Determine the payment method to use (from options or checkoutData)
      const paymentMethodToConfirm =
        options.paymentMethod || checkoutData.paymentMethod;

      if (paymentMethodToConfirm === "credit_card") {
        if (!options.paymentIntentId) {
          throw new Error(
            "Payment Intent ID is required for credit card confirmation."
          );
        }
        confirmationData.paymentIntentId = options.paymentIntentId;
        console.log(
          "useCheckout: Confirming Credit Card payment with backend",
          confirmationData
        );
        await checkoutService.confirmPayment(confirmationData);
        paymentConfirmed = true;
      } else if (paymentMethodToConfirm === "twint") {
        if (!options.paymentIntentId) {
          throw new Error(
            "Payment Intent ID is required for TWINT confirmation."
          );
        }
        confirmationData.paymentIntentId = options.paymentIntentId;

        // Check current payment status from Stripe if available
        if (stripe && options.paymentIntentId) {
          try {
            const { paymentIntent } = await stripe.retrievePaymentIntent(
              clientSecret // Use clientSecret from state to retrieve PI
            );
            console.log(
              `useCheckout: Current TWINT payment status: ${paymentIntent?.status}, ID: ${paymentIntent?.id}`
            );

            // Update confirmationData with any additional info from the paymentIntent
            if (paymentIntent && paymentIntent.id === options.paymentIntentId) {
              confirmationData.paymentStatus = paymentIntent.status;
            }
          } catch (err) {
            console.warn(
              "useCheckout: Could not retrieve payment intent from Stripe:",
              err
            );
            // Continue with backend confirmation even if Stripe direct check fails
          }
        }

        console.log(
          "useCheckout: Confirming TWINT payment with backend",
          confirmationData
        );
        await checkoutService.confirmPayment(confirmationData);
        paymentConfirmed = true;
      } else if (paymentMethodToConfirm === "bank_transfer_manual") {
        // For manual bank transfers, we just need to confirm the order with the backend
        // This will update status to awaiting_payment and decrease stock
        console.log(
          "useCheckout: Confirming Manual Bank Transfer payment with backend",
          confirmationData
        );
        await checkoutService.confirmPayment(confirmationData);
        paymentConfirmed = true;
      }

      if (paymentConfirmed) {
        console.log("useCheckout: Payment confirmed/initiated successfully.");
        dispatch(setPaymentCompleted(true));
        dispatch(setActiveStep(2)); // Move to step 2 (Review/Completion)
        dispatch(setLoading(false));
        return true;
      } else {
        throw new Error("Payment confirmation failed or method not handled.");
      }
    } catch (err) {
      console.error("useCheckout: Error processing payment:", err);
      dispatch(setError(err.message || t("checkout.paymentError")));
      dispatch(setLoading(false));
      return false;
    }
  };

  /**
   * Reset checkout state in Redux.
   */
  const resetCheckout = useCallback(() => {
    dispatch(resetCheckoutAction());
    console.log("useCheckout: Checkout state reset completely");
  }, [dispatch]);

  return {
    // State from Redux
    loading,
    error,
    checkoutData,
    checkoutSession,
    orderId,
    orderNumber,
    activeStep,
    paymentCompleted,
    clientSecret,

    // Stripe status
    stripeInitialized,
    stripeError,
    stripePromise: stripePromiseSingleton, // Provide the initialized Stripe promise

    // Actions/Handlers
    initStripe, // Expose if manual initialization is needed elsewhere (unlikely)
    updateCheckoutField,
    handleSameAsShipping,
    createCheckoutSession,
    processPayment,
    goToConfirmation,
    resetCheckout,
    setActiveStep: (step) => dispatch(setActiveStep(step)), // Allow manual step changes if needed

    // Cart context
    cart,
    clearCart,
    fetchCart,
  };
};
